import { useIsMutating, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import type { Brand } from "@shared/schema";
import { CUSTOMER_EVIDENCE_FLAG } from "@shared/customer-evidence";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";

type SettingValue = { brandId: string; settingKey: string; enabled: boolean };
type SettingsResponse = {
  settings: { key: string; defaultEnabled: boolean }[];
  values: SettingValue[];
};
const settingsKey = ["/api/admin/ads-express-brand-settings"];

function CustomerEvidenceConfigOwner({ brand }: { brand: Brand }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const mutationKey = ["customer-evidence-setting", brand.id];
  // Retain the lock even if this brand is unmounted and selected again mid-save.
  const saving = useIsMutating({ mutationKey }) > 0;
  const settings = useQuery<SettingsResponse>({
    queryKey: settingsKey,
    queryFn: async ({ signal }) => {
      const response = await fetch(settingsKey[0], { credentials: "include", signal });
      if (!response.ok) throw new Error("Could not load Customer Evidence Import settings.");
      const data: SettingsResponse = await response.json();
      if (!Array.isArray(data.settings) || !Array.isArray(data.values) ||
          !data.settings.some((item) => item.key === CUSTOMER_EVIDENCE_FLAG && typeof item.defaultEnabled === "boolean") ||
          data.values.some((item) => item.settingKey === CUSTOMER_EVIDENCE_FLAG && typeof item.enabled !== "boolean")) {
        throw new Error("Customer Evidence Import settings are unavailable.");
      }
      return data;
    },
    retry: false,
    staleTime: 0,
  });
  const definition = settings.data?.settings.find((item) => item.key === CUSTOMER_EVIDENCE_FLAG);
  const enabled = settings.data?.values.find(
    (item) => item.brandId === brand.id && item.settingKey === CUSTOMER_EVIDENCE_FLAG,
  )?.enabled ?? definition?.defaultEnabled;
  const save = useMutation({
    mutationKey,
    mutationFn: async (variables: { brandId: string; enabled: boolean }) => {
      const response = await apiRequest("PUT", `/api/admin/brands/${variables.brandId}/ads-express-settings`, {
        settingKey: CUSTOMER_EVIDENCE_FLAG,
        enabled: variables.enabled,
      });
      const data: { value: SettingValue } = await response.json();
      if (data.value?.brandId !== variables.brandId || data.value?.settingKey !== CUSTOMER_EVIDENCE_FLAG ||
          data.value?.enabled !== variables.enabled) {
        throw new Error("The saved Customer Evidence Import setting could not be confirmed. Reload to check its value.");
      }
      return data.value;
    },
    onSuccess: async (value, variables) => {
      // A late response only updates its own brand's row, never the selected brand.
      await queryClient.cancelQueries({ queryKey: settingsKey });
      queryClient.setQueryData<SettingsResponse>(settingsKey, (previous) => previous ? {
        ...previous,
        values: [...previous.values.filter((item) =>
          !(item.brandId === variables.brandId && item.settingKey === CUSTOMER_EVIDENCE_FLAG)), value],
      } : previous);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: settingsKey }),
        queryClient.invalidateQueries({
          predicate: (query) => query.queryKey.includes("customer-evidence") && query.queryKey.includes(variables.brandId),
        }),
        queryClient.invalidateQueries({
          predicate: (query) => query.queryKey.some((part) =>
            typeof part === "string" && (part === "beta-features" || part.endsWith("/beta-features"))),
        }),
      ]);
      toast({
        title: "Customer Evidence Import saved",
        description: `${brand.name}: ${value.enabled ? "enabled" : "disabled"}. Persona workflow URLs are unchanged.`,
      });
    },
    onError: (error: Error) => {
      // A failed request may have reached the server; re-read rather than assume.
      queryClient.invalidateQueries({ queryKey: settingsKey });
      toast({ title: "Customer Evidence Import save failed", description: error.message, variant: "destructive" });
    },
  });

  return (
    <Card data-testid="card-customer-evidence-config">
      <CardHeader>
        <CardTitle className="text-base">Customer Evidence Import</CardTitle>
        <CardDescription>
          Allow master admins to import audience evidence for {brand.name} under Knowledge Base &gt; Customer Evidence.
          This setting is independent of persona workflow URL overrides. Saving or clearing either does not change the other.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {settings.isError ? (
          <div role="alert" className="space-y-2 text-sm">
            <p className="text-destructive">{settings.error.message}</p>
            <Button variant="outline" size="sm" disabled={settings.isFetching} onClick={() => settings.refetch()}>
              Retry loading evidence settings
            </Button>
          </div>
        ) : settings.isPending || enabled === undefined ? (
          <p role="status" className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading evidence settings…
          </p>
        ) : (
          <div className="flex items-center justify-between gap-4">
            <label htmlFor={`customer-evidence-${brand.id}`} className="text-sm font-medium">
              Customer Evidence Import for {brand.name}
            </label>
            <Switch
              id={`customer-evidence-${brand.id}`}
              data-testid="switch-customer-evidence-import"
              checked={enabled}
              disabled={saving || settings.isFetching}
              onCheckedChange={(next) => save.mutate({ brandId: brand.id, enabled: next })}
            />
          </div>
        )}
        {saving && <p role="status" className="text-sm text-muted-foreground">Saving evidence setting…</p>}
        {save.isError && <p role="alert" className="text-sm text-destructive">{save.error.message}</p>}
      </CardContent>
    </Card>
  );
}

export function CustomerEvidenceConfigCard({ brand }: { brand: Brand }) {
  return <CustomerEvidenceConfigOwner key={brand.id} brand={brand} />;
}
